// Live's cuts (#239, #264; spec docs/specs/editor/captions.md): a look's paragraph settings and
// position cut words into captions, measured in the caption face. The face is measured first (it
// may load); an import then cuts its file's words, and a preset's click or a Paragraph change
// hands the measures to the timeline, which cuts the captions' own words as they are then
// (decision 3), so an edit made while the face loads is never lost.
import { liveFrame } from "../engine/project";
import type { AspectRatio } from "../project";
import { newId, type CaptionCut, type NewCaption } from "../timeline";
import type { CaptionSettings } from "./caption-style";
import { captionMetrics } from "./measure";
import { segmentWords } from "./segment";
import type { TimedWord } from "./words";

/** The caption face's measures for these words, on the project's frame. */
export async function captionCut(
  settings: CaptionSettings,
  words: readonly TimedWord[],
  aspectRatio: AspectRatio,
): Promise<CaptionCut> {
  const { paragraph } = settings;
  const metrics = await captionMetrics(
    settings.style,
    words.map((word) => word.text),
    paragraph.quantity > 1 || paragraph.autofit,
  );
  return { metrics, frame: liveFrame(aspectRatio) };
}

/** The words cut into captions by `settings` with these measures, each a new caption. */
export function cutWith(words: readonly TimedWord[], settings: CaptionSettings, cut: CaptionCut): NewCaption[] {
  return segmentWords(words, settings.paragraph, cut.frame, cut.metrics, settings.placement).map((segment) => ({
    ...segment,
    id: newId(),
  }));
}

/** The words cut into captions by `settings`, on the project's frame (an import's). */
export async function cutCaptions(
  words: readonly TimedWord[],
  settings: CaptionSettings,
  aspectRatio: AspectRatio,
): Promise<NewCaption[]> {
  return cutWith(words, settings, await captionCut(settings, words, aspectRatio));
}
